import React, { useState } from 'react';
import _ from 'lodash';
import { Container, Row, Col, Card as BSCard, CardTitle, Alert, Button, Modal, ModalHeader, ModalBody } from 'reactstrap';

import { assert } from '../base/utils/assert';
import C from '../C';
import DataStore from '../base/plumbing/DataStore';
import ActionMan from '../plumbing/ActionMan';
import { Card } from '../base/components/CardAccordion';
import { getType } from '../base/data/DataClass';
import Advert from '../base/data/Advert';
import NGO from '../base/data/NGO';

import PropControl, { setInputStatus, getInputStatuses, InputStatus, Help } from '../base/components/PropControl';
import { normaliseSogiveId } from '../base/plumbing/ServerIOBase';
import KStatus from '../base/data/KStatus';
import { CreateNGOForm, EditNGO } from './NGOPage';
import Icon from '../base/components/Icon';
import PropControlDataItem from '../base/components/propcontrols/PropControlDataItem';
import { space } from '../base/utils/miscutils';
import { A } from '../base/plumbing/glrouter';


/**
 * Are there obvious issues with any of the charities?
 * This would make for lots of process-update-render round-trips on every keystroke, so we debounce.
 */
const checkReady = _.debounce((charitiesList, path) => {
	const incompleteCharities = []; // every charity should have name and id
	const duplicateIds = []; // Charity IDs must be unique
	const ids = {}; // ids already seen
	charitiesList.forEach((charity, ci) => {
		if (charity) {
			if (!charity.id) {
				incompleteCharities.push(`No ID for "${charity.name || (ci+1)}"`);
				return;
			}
			if (!charity.name) incompleteCharities.push(`No name for [${charity.id}]`);
			if (ids[charity.id]) duplicateIds.push(charity.id);
			ids[charity.id] = true;
		}
	});

	if (!charitiesList.length) {
		setInputStatus({path, status: 'warning', message: 'No charities'});
	} else if (incompleteCharities.length) {
		setInputStatus({path, status: 'error', message: 'Incomplete charities: ' + incompleteCharities.join(', ')});
	} else if (duplicateIds.length) {
		setInputStatus({path, status: 'error', message: 'Duplicate charity IDs: ' + duplicateIds.join(', ')});
	} else {
		setInputStatus({path, status: null});
	}
}, 500);


function ParentControls({ path }) {
	const parentPath = path.concat('parent');
	const charity = DataStore.getValue(parentPath);

	let editRemove = null;
	if (charity) {
		const removeFn = () => {
			if (!window.confirm('Are you sure you want to remove the parent charity?')) return;
			DataStore.setValue(parentPath, null);
		};
		editRemove = <div className="ml-3 flex-column justify-content-center">
			<EditCharityModal path={parentPath} />
			<Button color="danger" className="mt-1" onClick={removeFn}>Remove parent charity</Button>
		</div>;
	}

	return <Row className="mt-2 justify-content-center">
		<Col xs="auto">
			<BSCard body className="flex-row justify-content-center align-items-center">
				<CharityLogoPreview charity={charity} optional className="mr-3" />
				<CharityPicker prop="parent" path={path} label="Parent charity (not normally needed)" className="m-0"
					help="Are the above entries actually projects of a single charity? If so, set the charity here."
				/>
				{editRemove}
			</BSCard>
		</Col>
	</Row>;
}


/**
 * className Normally blank!
 */
function CharityControls({entity, className = "charity-controls", ...other}) {
	let type = getType(entity);
	assert(C.TYPES.has(type), "CharityControls.jsx - no/bad type", entity);
	assert(entity.id, "CharityControls.jsx - no id", entity);
	let path = DataStore.getDataPath({status:C.KStatus.DRAFT, type, id:entity.id}).concat('charities');

	const charitiesList = Advert.charityList(entity).filter(x => x.id !== C.DEFAULT_AD_ID);

	// Is it ready (3 charities setup)?
	// TODO refactor this into Card??
	let issues = getInputStatuses(path);
	let warning = issues.filter(i => i.status === 'warning').map(i => InputStatus.str(i)).join(', ');
	let error = issues.filter(i => i.status === 'error').map(i => InputStatus.str(i)).join(', ');
	// let ready = isReady(charitiesList); ready? null : 'not complete'

	return (
		<Card title="Charities" warning={warning} error={error} className={className} {...other}>
			<Container fluid >
				<Row>
					<Col sm="4"><CharityForm entity={entity} i={0} charity={charitiesList[0]} /></Col>
					<Col sm="4"><CharityForm entity={entity} i={1} charity={charitiesList[1]} /></Col>
					<Col sm="4"><CharityForm entity={entity} i={2} charity={charitiesList[2]} /></Col>
				</Row>
				{(charitiesList.length === 1) ? <Row>
					<Col>This advert will use the single-charity design.</Col>
				</Row> : null}
				<ParentControls path={path} />
			</Container>
		</Card>
	);
}


function CharityLogoPreview({ charity, useAlt, optional, className, style, ...props }) {
	if (!charity) return null;
	const logoUrl = useAlt ? charity.altlogo : charity.logo;
	if (optional && !logoUrl) return null;

	// Parse out #ccrop:XX marker if present
	let ccrop = charity.circleCrop;
	if (logoUrl) {
		let wart = charity.logo.match(/#.*ccrop:(\d+)/);
		if (wart) ccrop = wart[1];
	}

	style = {...style}; // copy before mutate
	if (logoUrl) style.backgroundImage = `url("${logoUrl}")`;
	if (ccrop) style.backgroundSize = `${ccrop}%`;
	className = space(className, 'charity-logo-sample');

	return <div className={className} style={style} {...props} />;
}


const altLogoWarning = (
	<Help icon={<Icon name="warning" />} className="badge badge-danger">
		This charity doesn't have an alternative logo. You can set one up in the editor
		- or you can just edit the main logo on this advert's copy of the charity.
	</Help>
);


const altLogoHelp = 'Use the logo from this charity\'s "Alternative Logo" field. (Legacy control - feel free to just change the main logo on this advert\'s copy.)';
const nameInLogoHelp = 'Untick to have the advert show this charity\'s name under the logo.';
const deleteCharityWarning = 'Are you sure you want to completely remove this charity from the list? You will lose any local edits.';


/**
 * Charity picker, a few extra controls for display tweaks, and an editor for the embedded copy of the charity.
 * @param {Object} p
 * @param {!DataItem} p.entity
 */
export function CharityForm({entity, charity, i}) {
	assert(entity);
	if (!charity) charity = {};
	const type = getType(entity);
	const entityPath = DataStore.getDataPath({status: C.KStatus.DRAFT, type, id: entity.id});
	const listPath = entityPath.concat(['charities', 'list']);
	const charityPath = listPath.concat(i);
	const extraSettingsPath = entityPath.concat(['charities', 'extraSettings', charity.id || '-']);

	// Robust "is this a real thing" check, since it may be an empty object now
	const exists = !!Object.entries(charity).length;

	// Allow user to completely remove a charity from the list
	// Why? Because just emptying visible fields leaves stuff like @class intact & a ghost charity present.
	let clearButton = null;
	if (exists) {
		const doClear = () => {
			if (!window.confirm(deleteCharityWarning)) return;
			const charitiesPath = entityPath.concat('charities', 'list');
			const charities = DataStore.getValue(charitiesPath);
			charities.splice(i, 1);
			DataStore.setValue(charitiesPath, charities);
		};
		clearButton = <Button type="button" color="danger" className="mt-1" onClick={doClear}>Remove from this advert</Button>;
	}

	// Warn the user if they've ticked "Use alt logo" but this charity doesn't have one.
	const { useAltLogo } = DataStore.getValue(extraSettingsPath) || {};
	const altLogoError = (useAltLogo && !charity.altlogo) ? altLogoWarning : null;

	return (
		<BSCard body className={`ch${i + 1}`}>
			<CardTitle>Charity {i + 1}</CardTitle>
			{exists ? <>
				<h4 style={{color:charity.color}}>
					<a href={charity.url} target="_blank" rel="noreferrer">{charity.name || charity.id}</a>
				</h4>
				<div><small>Category: {charity.category || 'unset'}</small></div>
				<CharityLogoPreview charity={charity} useAlt={useAltLogo} />
				<PropControl prop="useAltLogo" type="checkbox" path={extraSettingsPath} label="Use alternative logo" help={altLogoHelp} disabled={!charity.altlogo} error={altLogoError} />
				<PropControl prop="nameInLogo" type="checkbox" path={charityPath} label="Logo contains name" help={nameInLogoHelp} dflt={true} />
			</> : null}
			<CharityPicker prop={i} path={listPath} />
			<EditCharityModal path={charityPath} />
			{charity.id ? null : <Alert color="info" className="small">Search for (or set up) a charity ID</Alert>}
			{clearButton}
		</BSCard>
	);
}


/**
 * Popup mini-editor for charities
 * @param {Object} p
 * @param {String[]} p.path Path to the local copy of the advert
 * @returns 
 */
function EditCharityModal({ path }) {
	const [show, setShow] = useState(false);
	const toggle = () => setShow(!show);

	const charity = DataStore.getValue(path);
	const cid = NGO.id(charity);
	if (!cid) return null;

	return <>
		<Button onClick={toggle}>Open charity editor</Button>
		<Modal size="xl" isOpen={show} toggle={toggle}>
			<ModalHeader toggle={toggle}>
				Edit charity {charity.name || charity.id}
			</ModalHeader>
			<ModalBody>
				<Alert color="warning flex-row">
					<div>
						You are editing a copy of this charity embedded in the advert.<br />
						Your changes will not automatically carry across to other adverts which use this charity.
					</div>
					<div className="ml-4 flex-row align-items-center">
						<A className="btn btn-primary" href={`/#ngo/${cid}`}>Edit main version</A>
					</div>
				</Alert>
				<EditNGO path={path} />
			</ModalBody>
		</Modal>
	</>;
}


/**
 * Make some tweaks to a charity provided by the back-end to improve adunit compatibility
 * @param {NGO} newNGO A charity object direct from the database
 * @returns {NGO}
 */
function transformCharity(newNGO) {
	// Removed use of normaliseSogiveId here - should be long-irrelevant to new adverts being edited
	// HACK SoGive's images -> photo ??maybe change SoGive here
	if (!newNGO.photo) newNGO.photo = newNGO.images;
	// prefer display name to official charity name
	if (newNGO.displayName) newNGO.name = newNGO.displayName;
	return newNGO;
}


/**
 * Boilerplate wrapper for a PCDI
 * @param {Object} p
 * @param {String[]} p.path Path to the charity
 */
function CharityPicker(props) {
	return <PropControlDataItem embed canCreate readOnly notALink
		modelValueFromInput={transformCharity}
		itemType={C.TYPES.NGO} status={KStatus.PUBLISHED} sort="id-asc"
		{...props}
	/>;
} // ./CharityPicker




export default CharityControls;
